import { IVitalSign } from '../../../store/slices/vitalSigns';

import { getTimeSlot } from '../../../utils/miscUtils';
import { useEffect, useState } from 'react';
import {
  BLOOD_PREASSURE_RANGE_RAPORT_CONFIG,
  BODY_COMPOSITION_RANGE_RAPORT_CONFIG,
  BODY_WEIGHT_RANGE_RAPORT_CONFIG,
  GLUCOSE_RANGE_RAPORT_CONFIG,
  GLUCOSE_UOM_IDS,
  OXYGEN_SATURATION_RANGE_RAPORT_CONFIG,
  PULSE_RANGE_RAPORT_CONFIG,
  TEMPERATURE_RANGE_RAPORT_CONFIG,
} from '../../../enums/enums';
import { useAppDispatch } from '../../../store/store';
import { getCustomerAttachments } from '../../../store/slices/documents';
import { toast } from 'react-toastify';
import BiomarkerRangeSvg from './BiomarkerRangeSvg';
import moment from 'moment';
import vitalSignResultStyle from '../VitalSignResultsV2.module.scss';
import styles from './VitalSignReport.module.scss';
import insightIcon from '../../../assets/icons/insights.svg';
import { useTranslation } from '../../../hooks/useTranslations';
import { isBodyComposition, isBodyWeight } from '../../../utils/vitalSignUtils';

interface VitalSignReportProps {
  report: Array<IVitalSign> | null;
  personAccountNumber?: number;
}

interface RangeConfiguration {
  rangeBarColors: string;
  width: string;
  barPosition: {
    [key: string]: string;
  };
}

const getResultColor = (vital: IVitalSign) =>
  vital.color_code?.toUpperCase() === 'FFFFFF' ? '333333' : vital.color_code;

export default function VitalSignReport({
  report,
  personAccountNumber,
}: VitalSignReportProps) {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();

  const handleDownload = async () => {
    const results = await dispatch(
      getCustomerAttachments({
        params: {
          date_to: moment(vitalSign?.datetime_measure).format('YYYY-MM-DD'),
          date_from: moment(vitalSign?.datetime_measure).format('YYYY-MM-DD'),
          group_info: {
            groupName: vitalSign?.vital_sign_group_name,
            groupId: vitalSign?.vital_sign_group_id_out,
          },
        },
        document_type: 'v',
        customer_person_id: personAccountNumber,
      })
    );

    if (results.type.includes('rejected')) {
      toast.error(translations('error.no_label_available'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  const [rangeConfiguration, setRangeConfiguration] = useState<RangeConfiguration | undefined>(
    undefined
  );

  const [vitalSign, setVitalSign] = useState<IVitalSign | undefined>(undefined);
  const showPlainVitals = !!report && report.length > 2 && isBodyComposition(report[0]);

  useEffect(() => {
    if (report?.length) {
      const initialVital = showPlainVitals
        ? report.find(isBodyWeight) ?? report[0]
        : report[0];
      switch (Number(initialVital.vital_sign_group_id_out)) {
        case 1:  // Temperature
          setRangeConfiguration(TEMPERATURE_RANGE_RAPORT_CONFIG);
          break;
        case 2: // Blood Pressure
          setRangeConfiguration(BLOOD_PREASSURE_RANGE_RAPORT_CONFIG);
          break;
        case 3: // Oxygen and Pulse
          setRangeConfiguration(OXYGEN_SATURATION_RANGE_RAPORT_CONFIG);
          break;
        case 4: // Body Composition
          if (isBodyWeight(initialVital)) {
            setRangeConfiguration(BODY_WEIGHT_RANGE_RAPORT_CONFIG);
          } else {
            setRangeConfiguration(BODY_COMPOSITION_RANGE_RAPORT_CONFIG);
          }
          break;
        case 5: // Glucose
          setRangeConfiguration(GLUCOSE_RANGE_RAPORT_CONFIG);
          break;

      }
      setVitalSign(initialVital);
    }
  }, [report, showPlainVitals]);

  const handleChangeVitalSign = (vitalSign: IVitalSign) => {
    let config;

    switch (Number(vitalSign.vital_sign_group_id_out)) {
      case 1: // Temperature
        config = TEMPERATURE_RANGE_RAPORT_CONFIG;
        break;

      case 2: // Blood Pressure
        if (['Diastolic', 'Systolic'].includes(vitalSign.property_name)) {
          config = BLOOD_PREASSURE_RANGE_RAPORT_CONFIG;
        } else {
          config = PULSE_RANGE_RAPORT_CONFIG;
        }
        break;

      case 3: // Oxygen + Pulse
        if (vitalSign.property_name === 'SpO2') {
          config = OXYGEN_SATURATION_RANGE_RAPORT_CONFIG;
        } else {
          config = PULSE_RANGE_RAPORT_CONFIG;
        }
        break;

      case 4: // Body Composition
        if (isBodyWeight(vitalSign)) {
          config = BODY_WEIGHT_RANGE_RAPORT_CONFIG;
        } else {
          config = BODY_COMPOSITION_RANGE_RAPORT_CONFIG;
        }
        break;

      case 5: // Blood Glucose
        config = GLUCOSE_RANGE_RAPORT_CONFIG;
        break;

      default:
        config = undefined;
    }

    setRangeConfiguration(config);
    setVitalSign(vitalSign);
  };

  return report && vitalSign && rangeConfiguration ? (
    <>
      <div className={vitalSignResultStyle.groupContainer}>
        <p className={styles.dateTime}>
          {moment(report[0].datetime_measure).format('DD MMMM, YYYY | hh:mm')}
        </p>
      </div>
      {report.length > 1 && !showPlainVitals && (
        <div className={styles.report}>
          <p>{translations('vitalsigns.report.label')}</p>
          <div className={styles.btnWrapper}>
            {report.map((el, idx) => {
              return (
                <button
                  className={vitalSign.property_name === el.property_name ? styles.active : ''}
                  style={{
                    color: `#${getResultColor(el)}`,
                    borderColor: `#${getResultColor(el)}`,
                    background: `#${getResultColor(el)}20`,
                  }}
                  key={idx}
                  onClick={() => handleChangeVitalSign(el)}
                >
                  {`${translations(`vitalSignProperties.${el.property_name.replace(/ /g, '_').toLowerCase()}`)}`}
                  <div style={{ fontSize: '20px', marginTop: '5px' }}>{el.property_value}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {(!isBodyComposition(vitalSign) || isBodyWeight(vitalSign)) && (
        <div className={styles.ranges}>
          <div className={styles.unitValue}>
            <p className={styles.value} style={{ color: `#${getResultColor(vitalSign)}` }}>
              {isBodyWeight(vitalSign)
                ? vitalSign?.result_weight
                : vitalSign.property_value
              }
            </p>
            {vitalSign.vital_sign_group_name === 'Blood Glucose' ? (
              <div className={styles.unit}>
                {GLUCOSE_UOM_IDS[vitalSign.property_uom_id as keyof typeof GLUCOSE_UOM_IDS]}
              </div>
            ) : (
              <div className={styles.unit}>{vitalSign.uom}</div>
          )}
        </div>
        <p className={`${styles.rangeName} ${styles.vitalSignName}`}>{vitalSign?.property_name}</p>
        <div className={styles.content} style={{ background: rangeConfiguration?.rangeBarColors }}>
          <div
            className={styles.values}
            style={{
              width: rangeConfiguration?.width,
              left: rangeConfiguration?.barPosition[vitalSign.reference_range_name],
            }}
          >
            <BiomarkerRangeSvg width="13" height="9" fill={`#${getResultColor(vitalSign)}`} />
          </div>
        </div>
        <p className={styles.rangeName} style={{ color: `#${getResultColor(vitalSign)}` }}>
          {translations(`biomarkerDetails.${vitalSign.reference_range_name.toLowerCase()}`)}
        </p>
      </div>
      )}
      <div className={styles.divider} />
      {!isBodyWeight(vitalSign) ?
        (<div className={styles.textLabel}>
          <label className={styles.label}>{translations('time.slot')}</label>
          <p className={styles.value}>
            {getTimeSlot(new Date(vitalSign.datetime_measure).getHours())}
          </p>
        </div>) :
        <div className={styles.textLabel}>
          <label className={styles.label}>BMI</label>
          <p className={styles.value} style={{ color: `#${getResultColor(vitalSign)}` }}>
            {vitalSign.property_value}
          </p>
        </div>
      }
      <div className={styles.divider} />
      {vitalSign.vital_sign_group_name === 'Blood Glucose' && (
        <div className={styles.textLabel}>
          <label className={styles.label}>{translations('vitalsigns.glucose.mealtime')}</label>
          <p className={styles.value}>
            {translations(`vitalsigns.mealTime.${vitalSign.mealtime_id}`)}
          </p>
        </div>
      )}
      {showPlainVitals && (
        <dl className={styles.vitalList}>
          {report.filter((item) =>
            !isBodyWeight(item) && item.property_name.trim().toLowerCase() !== 'bmi classification'
          ).map((item, index) => (
            <div className={styles.vitalRow} key={index}>
              <dt>
                {translations(
                  `vitalSignProperties.${item.property_name.replace(/ /g, '_').toLowerCase()}`
                )}
              </dt>
              <dd>
                {item.property_value}
                {item.uom && ` ${item.uom}`}
              </dd>
            </div>
          ))}
        </dl>
      )}
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div className={styles.insightContainer}>
            <img src={insightIcon} alt="insight" />
            {translations('biomarkerDetails.insights')}
          </div>
        </div>
        <div className={styles.body}>
          <div className={styles.card}>
            <p className={styles.title}>{translations('biomarkerDetails.what')}</p>
            <p className={styles.description}>{vitalSign?.reference_introductory_statement}</p>
          </div>
          <div className={styles.card}>
            <p className={styles.title}>{translations('biomarkerDetails.how')}</p>
            <p className={styles.description}>{vitalSign?.reference_summary}</p>
          </div>
        </div>
      </div>
    </>
  ) : null;
}
